import React, { useState } from "react";
import PropTypes from "prop-types";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CurrencyBitcoinOutlinedIcon from "@mui/icons-material/CurrencyBitcoinOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LimeCashierPreviewRoot from "./LimeCashierPreviewRoot";

const methods = [
  {
    id: "card",
    title: "Credit or debit card",
    description: "Pay with your credit or debit card",
    Icon: CreditCardOutlinedIcon,
  },
  {
    id: "bank",
    title: "Bank transfer",
    description: "Pay directly from your bank account",
    Icon: AccountBalanceOutlinedIcon,
  },
  {
    id: "wallet",
    title: "Digital wallet",
    description: "A quick way to pay with your wallet",
    Icon: AccountBalanceWalletOutlinedIcon,
  },
  {
    id: "crypto",
    title: "Cryptocurrency",
    description: "Pay with cryptocurrency",
    Icon: CurrencyBitcoinOutlinedIcon,
  },
  {
    id: "local",
    title: "Local payment methods",
    description: "More ways to pay in your country",
    Icon: LanguageOutlinedIcon,
  },
];

/** Previews Lime's method page and brand visibility without creating a payment session. */
export default function LimeCashierPreview({ brand, brandLogoUrl }) {
  const [selectedMethod, setSelectedMethod] = useState("card");
  const [failedLogoUrl, setFailedLogoUrl] = useState(null);
  const name = brand?.name?.trim() || "Demo merchant";
  const showTitle = brand?.headerVisibility?.enableTitle ?? true;
  const showAmount = brand?.headerVisibility?.enableAmount ?? false;
  const showPoweredBy = brand?.enablePoweredBy ?? true;

  return (
    <LimeCashierPreviewRoot aria-label="Lime cashier preview">
      <div className="lp-language">
        <div>
          <LanguageOutlinedIcon /> English <KeyboardArrowDownRoundedIcon />
        </div>
      </div>
      <div className="lp-checkout">
        <div className="lp-topbar">
          {showPoweredBy && (
            <div className="lp-wordmark" role="img" aria-label="CyberPay">
              Cyber
              <span>
                Pay
                <svg viewBox="0 0 90 12" preserveAspectRatio="none" aria-hidden="true">
                  <path
                    d="M3 7C24 3 50 10 87 5"
                    stroke="currentColor"
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
            </div>
          )}
          <span className="lp-secure">
            <LockOutlinedIcon />
            Secure checkout
          </span>
        </div>
        <div className="lp-columns">
          <aside className="lp-summary">
            <div className="lp-merchant-header">
              {showTitle && (
                <div className="lp-identity">
                  <div className="lp-merchant-mark">
                    {brandLogoUrl && brandLogoUrl !== failedLogoUrl ? (
                      <img
                        src={brandLogoUrl}
                        alt={name}
                        onError={() => setFailedLogoUrl(brandLogoUrl)}
                      />
                    ) : (
                      <StorefrontOutlinedIcon />
                    )}
                  </div>
                  <div>
                    <small>Paying to</small>
                    <strong>{name}</strong>
                  </div>
                </div>
              )}
              {showAmount && (
                <div className="lp-amount">
                  <small>Total to pay</small>
                  <div>
                    <span>€150.00</span>
                    <small>EUR</small>
                  </div>
                </div>
              )}
            </div>
            <div className="lp-summary-bottom">
              <img className="lp-art" src="/presets/lime/payment-art.webp" alt="" />
              <div className="lp-protection">
                <VerifiedUserOutlinedIcon />
                <div>
                  <strong>Your payment. Protected.</strong>
                  {showPoweredBy && <p>Handled securely by CyberPay.</p>}
                </div>
              </div>
            </div>
          </aside>
          <section className="lp-panel">
            <ol className="lp-progress" aria-label="Checkout progress">
              {["Payment", "Your details", "Confirmation"].map((step, index) => (
                <li key={step} aria-current={index === 0 ? "step" : undefined}>
                  <span>{index + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
            <div className="lp-heading">
              <h2>
                Choose how to <span>pay.</span>
              </h2>
              <p>Your payment, your preferred method.</p>
            </div>
            <div className="lp-methods" aria-label="Payment methods">
              {methods.map(({ id, title, description, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className="lp-method"
                  aria-pressed={selectedMethod === id}
                  onClick={() => setSelectedMethod(id)}
                >
                  <span className="lp-method-icon">
                    <Icon />
                  </span>
                  <span className="lp-method-copy">
                    <strong>{title}</strong>
                    <small>{description}</small>
                  </span>
                  <span className="lp-radio" aria-hidden="true" />
                </button>
              ))}
            </div>
            <div className="lp-actions">
              <button type="button" className="lp-continue">
                Continue
                <ArrowForwardRoundedIcon />
              </button>
              <p>
                <LockOutlinedIcon />
                Your payment details are kept private.
              </p>
            </div>
          </section>
        </div>
        <div className="lp-footer">
          {showPoweredBy && (
            <span>
              Payments powered by <strong>CyberPay</strong>
            </span>
          )}
          <span className="lp-cancel">
            <ArrowBackRoundedIcon />
            Cancel payment
          </span>
        </div>
      </div>
    </LimeCashierPreviewRoot>
  );
}

LimeCashierPreview.propTypes = {
  brand: PropTypes.shape({
    name: PropTypes.string,
    enablePoweredBy: PropTypes.bool,
    headerVisibility: PropTypes.shape({
      enableTitle: PropTypes.bool,
      enableAmount: PropTypes.bool,
    }),
  }),
  brandLogoUrl: PropTypes.string,
};
